import type {
  AccountsData,
  AnalystThreadDetail,
  AnalystThreadSummary,
  AnalystTurnEvent,
  ClientsData,
  DeliveryManagerData,
  DeliveryOverviewData,
  DeliveryRecruiterData,
  DeliveryScopeType,
  DeliveryTimeseriesData,
  Envelope,
  Grain,
  Me,
  OverviewData,
  PeriodKey,
  RecruiterOffering,
  RegionAccountsData,
  RegionDetailData,
  Source,
  Specialty,
  SpecialtiesData,
  SyncMeta,
  Targeting,
  TimeseriesData,
  UsersResponse,
  JobsExportEvent,
  ExportSizeData,
  ExportDataset,
  AuditListData,
  AuditResultsData,
  AuditRunsData,
  AlertsData,
  AlertEmailsPanelData,
} from './types.js';
import type { UiRegion } from '../theme/tokens.js';
import { scopeAccounts, scopeOverview, scopeSpecialties } from './regionScope.js';

/**
 * The typed fetch client.
 *
 * Envelope unwrapping happens here, once, so no component has to remember that snapshot data
 * arrives wrapped. Crucially, unwrapping **keeps the freshness fields**: `data` alone would
 * discard `asOf`, `syncStatus`, and `dataQuality`, and FR-015 requires every view carrying
 * snapshot data to expose the as-of date. Returning the whole envelope makes that structural
 * rather than something each caller must opt into.
 *
 * For the same reason, the three payloads carrying a *list of regions* are scrubbed here as they
 * land (`regionScope.ts`): the server still serves seven and this client renders six, and the
 * store caches whatever comes back for six hours, so a page filtering for itself would be
 * filtering something the prefetch had already stored unfiltered.
 */

const API_BASE = (import.meta.env?.VITE_API_ORIGIN as string | undefined) ?? '';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }

  /** The session is gone or was never there — the caller should send them to sign in. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** The account exists but was revoked — a different message from "please sign in". */
  get isDeactivated(): boolean {
    return this.code === 'ACCOUNT_DEACTIVATED';
  }
}

/**
 * Told whenever ANY request finds the session gone (401) or the account deactivated.
 *
 * ⚠ **The session is checked once at load (`/api/auth/me`) and never again by the app**, so
 * without this a session that expires mid-use surfaces as every page's own error banner — `Could
 * not load the Regions overview. Sign in to continue.` beside a Retry button that can only 401
 * again. Pages paint from the client store, so that banner can be the first sign of trouble hours
 * after the session actually ended. Raising it here, where every response already passes, means
 * no page, export or analyst turn has to remember to.
 *
 * Safe to treat every 401 this way because the server reserves the status for the READER's
 * session: an upstream credential failure is a 502 (`AUDIT_UPSTREAM_AUTH`) and a Google 401 on an
 * export becomes a 200 `consent_required`, precisely so neither reads as the reader being signed
 * out.
 */
type AuthLostListener = (error: ApiError) => void;

const authLostListeners = new Set<AuthLostListener>();

export function onAuthLost(listener: AuthLostListener): () => void {
  authLostListeners.add(listener);
  return () => {
    authLostListeners.delete(listener);
  };
}

/** Builds the error for a failed response, announcing it first if the session is the cause. */
function failure(status: number, body: unknown): ApiError {
  const parsed = body as { error?: { code?: string; message?: string } } | null;
  const error = new ApiError(
    status,
    parsed?.error?.code ?? 'UNKNOWN',
    parsed?.error?.message ?? 'Request failed.',
  );
  if (error.isUnauthenticated || error.isDeactivated) {
    for (const listener of authLostListeners) listener(error);
  }
  return error;
}

type Query = Record<string, string | number | boolean | null | undefined>;

/** The four parameters every Delivery table endpoint accepts. */
export interface DeliveryTableParams {
  period: PeriodKey;
  source: Source;
  targeting: Targeting;
  specialty: Specialty;
}

function buildUrl(path: string, query?: Query): string {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, String(value));
  }
  return url.toString();
}

async function request<T>(path: string, init?: RequestInit & { query?: Query }): Promise<T> {
  const { query, ...rest } = init ?? {};

  const response = await fetch(buildUrl(path, query), {
    // The session cookie is httpOnly, so it has to ride the request rather than be read.
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(rest.headers ?? {}) },
    ...rest,
  });

  if (response.status === 204) return undefined as T;

  const body = await response.json().catch(() => null);

  if (!response.ok) throw failure(response.status, body);

  return body as T;
}

/**
 * Reads a newline-delimited JSON stream, handing each event to `onEvent` as it lands.
 *
 * ⚠ **The whole point is not to wait for the body.** A big export is ~74 seconds, so `.json()`
 * here — or any buffering — would deliver thirteen progress events simultaneously at the end,
 * which is indistinguishable from sending none.
 *
 * ⚠ **A chunk boundary is not a line boundary.** The reader hands back arbitrary byte runs, so a
 * JSON object can and does arrive split across two of them. The trailing partial line is carried
 * over in `buffer` rather than parsed — dropping it loses events at random under load, which is
 * the kind of bug that only shows up on the slowest, largest export.
 */
async function streamNdjson<E>(
  path: string,
  body: unknown,
  onEvent: (event: E) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(buildUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    /*
     * ⚠ The analyst's turn is cancellable (spec 006, FR-A70a–e): asking again, closing the modal,
     * navigating away and a dropped connection all cancel through ONE mechanism, and aborting
     * the fetch is what lets the server cancel the BigQuery job behind it. A scan nobody will
     * read is still billed.
     */
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) throw failure(response.status, await response.json().catch(() => null));

  const reader = response.body?.getReader();
  if (!reader) throw new ApiError(500, 'UNKNOWN', 'This browser cannot read a streamed response.');
  // Cancelling the reader is what actually closes the connection; aborting the fetch alone can
  // leave the body draining.
  signal?.addEventListener('abort', () => void reader.cancel(), { once: true });

  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    // The last element is whatever came after the final newline — a partial line, or ''.
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.trim()) continue;
      onEvent(JSON.parse(line) as E);
    }
  }

  if (buffer.trim()) onEvent(JSON.parse(buffer) as E);
}

/**
 * The four table parameters, and **only** those four.
 *
 * ⚠ **Spreading the caller's object instead of picking is how this broke.** `DELIVERY_DEFAULTS`
 * carries a fifth field, `recruiterOffering`, because the *manager* state has that control — and
 * `/api/delivery` and `/api/delivery/leaders/:key` reject it with a `422`, correctly: quietly
 * accepting a filter an endpoint does not honour looks exactly like a bug (see `rejectUnknown`).
 *
 * The page never hit this because it builds its own four-field object. The **prefetcher** passes
 * `DELIVERY_DEFAULTS` straight through, so every leader and the all-managers view failed to
 * prefetch and loaded cold — visible only as 422s in the server log. Two callers of one request
 * with no reason to meet, which is the same failure mode `store/queries.ts` exists to prevent for
 * cache keys.
 *
 * Picking here rather than fixing the one caller means a future caller cannot reintroduce it.
 * TypeScript will not catch it: excess-property checks do not apply to a variable, only to an
 * object literal.
 */
function tableQuery(params: DeliveryTableParams): Query {
  return {
    period: params.period,
    source: params.source,
    targeting: params.targeting,
    specialty: params.specialty,
  };
}

export const api = {
  // ---- Auth --------------------------------------------------------------
  me: () => request<Me>('/api/auth/me'),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),
  /** Full navigation, not fetch: the OAuth flow is a browser redirect. */
  signInHref: () => `${API_BASE}/api/auth/google`,

  // ---- Export ------------------------------------------------------------
  /**
   * Whether this account has already let the app write a Google Sheet for it, and whether this
   * deployment has a Sheets destination configured at all.
   *
   * Asked so the button can say what it is about to do. A reader who presses Export and lands
   * on a Google consent screen with no warning has been surprised by their own dashboard.
   */
  exportGrantStatus: () =>
    request<{ hasGrant: boolean; configured: boolean }>('/api/export/google/status'),

  /**
   * How big an export would be — so an impossible one is a disabled control that says why,
   * rather than a refusal after the click (FR-090a).
   *
   * ⚠ Same route shape as the export itself: `null` is every region, and the window is the
   * grain or the period, never both.
   */
  exportSize: (
    regionCode: string | null,
    params: { grain?: Grain; period?: PeriodKey; clientKey?: string; dataset?: ExportDataset },
  ) =>
    request<Envelope<ExportSizeData>>(
      regionCode == null
        ? '/api/regions/export/size'
        : `/api/regions/${encodeURIComponent(regionCode)}/export/size`,
      { query: params as Query },
    ),

  /**
   * How big a per-client export would be (FR-092) — every account in the region, two tabs each.
   *
   * ⚠ A different route from `exportSize`, not a flag: that one answers for a region or for one
   * account, and this file charges a header row per tab across up to 48 tabs. Same arithmetic in
   * two places is how a control comes to say "this fits" about an export the server refuses.
   */
  exportClientsSize: (regionCode: string, params: { period: PeriodKey; targeting: Targeting }) =>
    request<Envelope<ExportSizeData & { clientCount: number }>>(
      `/api/regions/${encodeURIComponent(regionCode)}/export/clients/size`,
      { query: params as Query },
    ),

  /**
   * Exports **every account** in the region's By Client breakdown, two tabs each (FR-092).
   *
   * ⚠ It carries `targeting` as well as `period`, because on that card targeting decides which
   * accounts are listed at all — and therefore how many tabs the file has. A grain is refused:
   * that card has no grain control.
   */
  exportRegionClients: (
    regionCode: string,
    params: { period: PeriodKey; targeting: Targeting; returnTo: string },
    onEvent: (event: JobsExportEvent) => void,
  ) =>
    streamNdjson(
      `/api/regions/${encodeURIComponent(regionCode)}/export/clients`,
      params,
      onEvent,
    ),

  revokeExportGrant: () =>
    request<{ hasGrant: boolean }>('/api/export/google/grant', { method: 'DELETE' }),

  /**
   * Exports a region's raw jobs to a Google Sheet over the chart's current window.
   *
   * ⚠ **`consent_required` is a normal result, not an error.** The first export by any user has
   * no Drive grant, so the server answers 200 with somewhere to send them. Branching on this in
   * a `catch` would mean treating an ordinary step in the flow as a failure.
   */
  /**
   * Exports one region, or **every** region when `regionCode` is null (FR-090).
   *
   * ⚠ `null` takes a **different path**, not `regionCode=ALL`: `ALL` is a chart scope and is
   * deliberately absent from `UiRegion` so it cannot leak into a pill or a table row, and the
   * per-region route still refuses the word.
   *
   * ⚠ **`grain` or `period`, never both.** The chart export is cut by the grain it is drawing;
   * the Region Performance export is cut by that table's period. The server refuses both at once
   * rather than inventing a precedence a reader cannot see.
   *
   * ⚠ **`clientKey` narrows it to one account and rides the SAME route** (FR-091) — it is not a
   * third path. A client export is always inside one region, because the breakdown is
   * region-scoped and the stored rows are filed under `(uiRegion, clientKey)`; the all-regions
   * route refuses the parameter rather than reading it as "this account everywhere".
   *
   * ⚠ **`dataset` narrows WHICH TABS, and is a different axis from every other parameter here**
   * (FR-093). `grain`/`period` cut the window and `clientKey` cuts the rows; this one decides
   * whether the file has a Jobs tab, a Submissions tab, or both. It exists for the chart matrix's
   * action column, where the row a reader presses IS the dataset — omit it and the JOBS row and
   * the SUBS row would write byte-identical files.
   */
  exportRegionJobs: (
    regionCode: string | null,
    params: ({ grain: Grain } | { period: PeriodKey }) & {
      returnTo: string;
      clientKey?: string;
      dataset?: ExportDataset;
    },
    onEvent: (event: JobsExportEvent) => void,
  ) =>
    streamNdjson(
      regionCode == null
        ? '/api/regions/export/jobs'
        : `/api/regions/${encodeURIComponent(regionCode)}/export/jobs`,
      params,
      onEvent,
    ),

  /**
   * Exports one Delivery scope's submissions to a Google Sheet (FR-D27).
   *
   * ⚠ **One route for all four scopes**, unlike the region export's two. `ALL` had to take a
   * separate path there because it is a chart scope with no legitimate spelling in a region
   * parameter; Delivery's `all` is a first-class member of `DeliveryScopeType` and `ALL` is its
   * key, the same pair `/api/delivery/timeseries` already takes.
   *
   * ⚠ **`period`, never `grain`.** Both of this page's export controls sit on the Managers or
   * Recruiters table, whose activity columns are cut by the Timeline control — so the file is cut
   * by the same thing the Subs cell it came from is.
   *
   * ⚠ **Submissions only, so there is no `jobs` spelling to reach for.** A job has no recruiter,
   * which is why this page has no JOBS series and no Jobs column either.
   */
  exportDeliverySubmissions: (
    scope: { scopeType: DeliveryScopeType; scopeKey: string },
    params: { period: PeriodKey; returnTo: string },
    onEvent: (event: JobsExportEvent) => void,
  ) => streamNdjson('/api/delivery/export/submissions', { ...scope, ...params }, onEvent),

  /**
   * How big a Delivery export would be.
   *
   * ⚠ Served but **not called on mount** by the control, deliberately — see the route's own note.
   * The largest possible Delivery export is 4.5% of Google's ceiling, so asking once per table
   * plus once per row would be spending a request to be told "yes". It is here so the answer
   * stays checkable the day the roster or the window grows.
   */
  deliveryExportSize: (scope: { scopeType: DeliveryScopeType; scopeKey: string }, period: PeriodKey) =>
    request<Envelope<ExportSizeData>>('/api/delivery/export/size', {
      query: { ...scope, period } as Query,
    }),

  // ---- Regions -----------------------------------------------------------
  overview: (period: PeriodKey) =>
    request<Envelope<OverviewData>>('/api/regions/overview', { query: { period } }).then(
      scopeOverview,
    ),

  regionDetail: (regionCode: string) =>
    request<Envelope<RegionDetailData>>(`/api/regions/${encodeURIComponent(regionCode)}`),

  /**
   * ⚠ **`scopeType` and `scopeKey` travel together or not at all.** The route refuses half a
   * scope rather than defaulting the level, because guessing `client` for a facility key charts
   * a parent and finds nothing. Both are dropped when either is missing.
   */
  timeseries: (
    regionCode: string,
    params: {
      grain: Grain;
      source: Source;
      targeting: Targeting;
      specialty: Specialty;
      scopeType?: 'client' | 'msp' | 'child' | null;
      scopeKey?: string | null;
    },
  ) => {
    const { scopeType, scopeKey, ...rest } = params;
    const scoped = scopeType && scopeKey ? { scopeType, scopeKey } : {};

    return request<Envelope<TimeseriesData>>(
      `/api/regions/${encodeURIComponent(regionCode)}/timeseries`,
      { query: { ...rest, ...scoped } },
    );
  },

  regionAccounts: (regionCode: string) =>
    request<Envelope<RegionAccountsData>>(
      `/api/regions/${encodeURIComponent(regionCode)}/accounts`,
    ),

  clients: (regionCode: string, params: { period: PeriodKey; targeting: Targeting }) =>
    request<Envelope<ClientsData>>(`/api/regions/${encodeURIComponent(regionCode)}/clients`, {
      query: params,
    }),

  // ---- Delivery ----------------------------------------------------------
  //
  // Table filters ride the request here, unlike the Regions overview: a Delivery row carries 21
  // metric columns across 12 combinations, and the four states each read a different collection,
  // so the server picks the combination rather than shipping every scope's whole map.

  /**
   * The whole Specialties page in one request (contracts/specialties.md).
   *
   * ⚠ No `period`, `source` or `targeting` — the server returns 422 for each, because this page
   * has no period-scoped column and exposes internal/target as columns rather than filters.
   * Selecting a discipline needs no request at all: the strip re-scopes from `byRegion`.
   */
  specialties: (params: { region?: UiRegion } = {}) =>
    request<Envelope<SpecialtiesData>>('/api/specialties', { query: { ...params } }).then(
      scopeSpecialties,
    ),

  delivery: (params: DeliveryTableParams) =>
    request<Envelope<DeliveryOverviewData>>('/api/delivery', { query: tableQuery(params) }),

  deliveryLeader: (leaderKey: string, params: DeliveryTableParams) =>
    request<Envelope<DeliveryOverviewData>>(
      `/api/delivery/leaders/${encodeURIComponent(leaderKey)}`,
      { query: tableQuery(params) },
    ),

  /** Two or more leaders at once (FR-D07a). `setKey` is the newline-joined, sorted key list. */
  deliveryLeaders: (setKey: string, params: DeliveryTableParams) =>
    request<Envelope<DeliveryOverviewData>>('/api/delivery/leaders', {
      query: { ...tableQuery(params), leaderKeys: setKey },
    }),

  deliveryManager: (
    managerId: string | number,
    params: DeliveryTableParams & { recruiterOffering?: RecruiterOffering },
  ) =>
    request<Envelope<DeliveryManagerData>>(
      `/api/delivery/managers/${encodeURIComponent(String(managerId))}`,
      { query: { ...params } },
    ),

  deliveryRecruiter: (recruiterId: string | number, params: DeliveryTableParams) =>
    request<Envelope<DeliveryRecruiterData>>(
      `/api/delivery/recruiters/${encodeURIComponent(String(recruiterId))}`,
      { query: tableQuery(params) },
    ),

  /**
   * The scoped chart. Separate from the table calls for the same reason it is on Regions: the
   * chart filters change independently, and bundling them would refetch a 21-column table on
   * every chart interaction.
   *
   * `scopeKey` is omitted for the `all` scope — the endpoint refuses a key there rather than
   * ignoring one, because "the company, filtered to one manager" is not a question it answers.
   */
  deliveryTimeseries: (params: {
    scopeType: DeliveryScopeType;
    scopeKey?: string | null;
    grain: Grain;
    source: Source;
    targeting: Targeting;
    specialty: Specialty;
  }) =>
    request<Envelope<DeliveryTimeseriesData>>('/api/delivery/timeseries', {
      query: {
        ...params,
        scopeKey: params.scopeType === 'all' ? undefined : (params.scopeKey ?? undefined),
      },
    }),

  // ---- Account search ----------------------------------------------------
  searchAccounts: (q: string, limit = 10) =>
    request<Envelope<AccountsData>>('/api/accounts/search', { query: { q, limit } }).then(
      scopeAccounts,
    ),

  // ---- Meta --------------------------------------------------------------
  sync: () => request<SyncMeta>('/api/meta/sync'),

  // ---- Audit page (spec 007, contracts/audit-api.md) ----
  auditList: () => request<Envelope<AuditListData>>('/api/audit'),
  // ---- Alerts (spec 008) — no parameters: filtering is the page's, over one cached response ----
  auditAlerts: () => request<Envelope<AlertsData>>('/api/audit/alerts'),
  auditRuns: (auditId: string, params: { before?: string; limit?: number } = {}) =>
    request<Envelope<AuditRunsData>>(`/api/audit/${encodeURIComponent(auditId)}/runs`, {
      query: { ...params },
    }),
  auditResults: (auditId: string, runDate: string) =>
    request<Envelope<AuditResultsData>>(
      `/api/audit/${encodeURIComponent(auditId)}/runs/${encodeURIComponent(runDate)}`,
    ),

  // ---- Settings (admin only; the server enforces it) ---------------------
  users: (q?: string) => request<UsersResponse>('/api/settings/users', { query: { q } }),
  /** Spec 008 — the third admin capability: alert email counts, read-only. */
  alertEmails: () => request<AlertEmailsPanelData>('/api/settings/alert-emails'),

  changeRole: (email: string, role: 'user' | 'admin') =>
    request<{ user: unknown }>(`/api/settings/users/${encodeURIComponent(email)}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),

  changeStatus: (email: string, isActive: boolean) =>
    request<{ user: unknown }>(`/api/settings/users/${encodeURIComponent(email)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive }),
    }),

  /**
   * Drops the server's cached responses for the active snapshot — the server half of Settings'
   * *Hard refresh*.
   *
   * ⚠ It does **not** re-run the ingest, and no endpoint does: Principle II forbids a
   * request-time path into the daily job, which is why that job has no HTTP surface at all. What
   * this changes is that nothing cached stands between a reader and the promoted snapshot; the
   * snapshot itself moves only when the ingest runs. The response names it so the caller can say
   * so honestly.
   */
  purgeServerCache: () =>
    request<{ snapshotId: string; asOf: string | null; purgedKeys: number }>(
      '/api/settings/cache/refresh',
      { method: 'POST' },
    ),

  // ---- Analyst (spec 006) -------------------------------------------------
  /**
   * Ask the analyst. Streams NDJSON from the POST itself.
   *
   * ⚠ **Not `EventSource`.** The browser's EventSource can only issue a bodyless GET, so it
   * cannot carry the question — using it would force a create-then-subscribe handshake and
   * server-side handoff state invented purely to satisfy a transport.
   *
   * ⚠ **Rows arrive BEFORE the prose**, and that ordering is deliberate: the table is the answer
   * and the sentence is the caption. A reader given prose first and a table thirty seconds later
   * has been handed the least verifiable part first.
   *
   * ⚠ Pass a `signal` for every turn. Asking again, closing the modal, navigating away and a
   * dropped connection all cancel through one mechanism, and the abort is what lets the server
   * cancel the warehouse query behind it.
   */
  analystTurn: (
    body: { threadId?: string; question: string },
    onEvent: (event: AnalystTurnEvent) => void,
    signal?: AbortSignal,
  ) => streamNdjson<AnalystTurnEvent>('/api/analyst/turn', body, onEvent, signal),

  /**
   * Export a stored result to Google Sheets.
   *
   * ⚠ Addressed by `resultId`, so it exports **the result the reader was given** — never a
   * re-run, which can return different rows, and never the displayed page, which is smaller than
   * the file by design.
   */
  analystExport: (
    body: { resultId: string; returnTo?: string },
    onEvent: (event: JobsExportEvent) => void,
  ) => streamNdjson<JobsExportEvent>('/api/analyst/export', body, onEvent),

  analystThreads: () => request<{ threads: AnalystThreadSummary[] }>('/api/analyst/threads'),

  /**
   * Admin: one person's threads.
   *
   * ⚠ `owner` is required by the server — this looks a named person up rather than browsing
   * everyone's conversations, and every read is audited with both addresses.
   */
  analystAdminThreads: (owner: string) =>
    request<{ threads: AnalystThreadSummary[] }>(
      `/api/analyst/admin/threads?owner=${encodeURIComponent(owner)}`,
    ),
  analystAdminThread: (threadId: string) =>
    request<AnalystThreadDetail>(
      `/api/analyst/admin/threads/${encodeURIComponent(threadId)}`,
    ),
  analystThread: (threadId: string) =>
    request<AnalystThreadDetail>(`/api/analyst/threads/${encodeURIComponent(threadId)}`),

};

export type Api = typeof api;
